import { DialogModule } from '@angular/cdk/dialog';
import { LayoutModule } from '@angular/cdk/layout';
import { TextFieldModule } from '@angular/cdk/text-field';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { InitialLoadingComponent } from '@app/shared/components/initial-loading/initial-loading.component';
import { AuthGuard } from '@app/shared/guards/auth-guard';
import { ErrorInterceptor } from '@app/shared/interceptors/error-interceptor';
import { environment } from '@environment/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { RendererHttpInterceptor } from 'src/adapters/renderer-http-interceptor';
import { RENDERER_AUTH_TOKEN, RENDERER_AUTH_TOKEN_PROVIDER } from 'src/providers/renderer-auth-token-provider';
import { RENDERER_HTTP_CLIENT_PROVIDER } from 'src/providers/renderer-http-client-provider';
import { RENDERER_TOKEN_STORE_PROVIDER } from 'src/providers/renderer-token-store-provider';
import { SHARED_SOCKET_SERVICE_PROVIDER } from 'src/providers/shared-socket-service-provider';

import { ElectronInvocationChannel } from '../../../shared/src/types';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthService } from './auth/services/auth.service';
import { AppInitializerService } from './core/services/app-initializer.service';
import { ElectronService } from './electron/electron.service';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    MatDialogModule,
    BrowserModule,
    AppRoutingModule,
    DialogModule,
    ReactiveFormsModule,
    MatMomentDateModule,
    MatSnackBarModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    LayoutModule,
    MatSelectModule,
    MatAutocompleteModule,
    BrowserAnimationsModule,
    ServiceWorkerModule.register('../service-worker.js', {
      // Disable service worker in Electron (file:// protocol) even if environment enables it
      enabled: environment.serviceWorker && window.location.protocol !== 'file:',
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    MatIconModule,
    MatInputModule,
    TextFieldModule,
    MatCheckboxModule,
    MatButtonModule,
    MatRadioModule,
    MatStepperModule,
    InitialLoadingComponent,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    RENDERER_HTTP_CLIENT_PROVIDER,
    RENDERER_TOKEN_STORE_PROVIDER,
    SHARED_SOCKET_SERVICE_PROVIDER,
    RENDERER_AUTH_TOKEN_PROVIDER,
    AuthGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RendererHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (injector: Injector) => {
        return async () => {
          // Note: Guest meeting routes now use GuestAppModule and never reach this initializer
          // This APP_INITIALIZER only runs for authenticated routes

          // Handle OAuth authorization code FIRST, before any other initialization
          const urlParams = new URLSearchParams(window.location.search);
          const code = urlParams.get('code');

          if (code) {
            console.log('[APP-INITIALIZER] OAuth code found in URL, processing before app initialization');
            try {
              const authTokenAdapter = injector.get(RENDERER_AUTH_TOKEN);
              const authService = injector.get(AuthService);

              // Get auth redirect URL
              const authStrings = authService.getAuthStrings();

              // Exchange authorization code for tokens
              await authTokenAdapter.exchangeAuthorizationCodeForValidAccessToken(code, authStrings.authRedirectURL);

              console.log('[APP-INITIALIZER] OAuth token exchange completed successfully');

              // Clean URL to remove OAuth parameters
              window.history.replaceState({}, document.title, window.location.pathname);
            } catch (error) {
              console.error('[APP-INITIALIZER] OAuth token exchange failed:', error);
              const authUrl = await injector.get(AuthService).getAuthUrl();
              document.location.replace(authUrl);
            }
          }

          // Handle masquerade token - must happen before app initialization to prevent race with AuthGuard
          const authService = injector.get(AuthService);
          const electronService = injector.get(ElectronService);

          // In Electron, the deep link arrives in the main process and is buffered;
          // the renderer pulls it here. In the browser, the token rides on the URL.
          let masqueradeToken = urlParams.get('token');
          if (!masqueradeToken && electronService.isRunningInElectron) {
            masqueradeToken = await electronService.invoke(
              ElectronInvocationChannel.ConsumePendingMasqueradeToken,
              undefined
            );
          }

          if (masqueradeToken) {
            const success = await authService.updateWithMasqueradeToken(masqueradeToken);

            if (success) {
              window.history.replaceState({}, document.title, window.location.pathname);
              window.location.reload();
              return;
            }
            // On failure, keep token in URL for retry - error already logged in updateWithMasqueradeToken
          }

          // Run normal app initialization
          const initializer = injector.get(AppInitializerService);
          initializer.init();
        };
      },
      deps: [Injector],
      multi: true,
    },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } },
    provideEnvironmentNgxMask(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        logErrors: true,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(_: Sentry.TraceService) {}
}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  // Use relative path for file:// protocol (Electron), absolute path for http(s)://
  const prefix = window.location.protocol === 'file:' ? './assets/i18n/' : '/assets/i18n/';
  return new TranslateHttpLoader(http, prefix, '.json');
}
